import { Component, computed, inject, input, model, output, ViewEncapsulation } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { FtTasksService } from 'apps/account/src/account-shared/services/ft-tasks.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { CurationBackgroundImageDirective } from '../../stories/curation-card/background-image.directive';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { FtImageComponent } from './ft-image.component';

/**
 * Native port of the AngularJS `ftEmailImagesPreview` component — the email-banner thumbnail
 * preview in the contest Details sheet. Renders the `previewImage` (default-language-or-first,
 * falling back to the first processed one) as a `.thumbnail-image` desktop (+ mobile when
 * `hasMobile`) via `ftBackgroundImage` + `buildAzureStoredPhotoUrl`.
 *
 * Also hosts the previews GALLERY modal (v1 emailImagesPreview.html:25-82, the
 * `modals.emailImages.{type}.preview` ftModal the read-view panel targeted): one panel per
 * language with a desktop/mobile switcher, a Refresh button and the language code. Opened by the
 * parent through `[(open)]` (the v1 `ft-targets-modal` attribute on the panel). Refresh follows
 * the v1 split: this component registers the `generate-email-image-{imageUUID}-{code}` task and
 * emits `(refresh)`; the parent calls the generate API and finishes the same task key.
 */
@Component({
    selector: 'ft-email-images-preview-ng',
    imports: [TranslateModule, CurationBackgroundImageDirective, FtModalComponent, FtModalChromeComponent, FtImageComponent],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-email-images-preview.component.html'
})
export class FtEmailImagesPreviewComponent {
    /** The email images array (`data.contest.prearrivalEmailImages.item` / poststay). */
    readonly images = input<any[] | undefined>();

    /** 'prearrival' | 'poststay' (| 'confirmation') — drives the desktop CDN suffix. */
    readonly emailImageType = input('');

    /** ft-show/hide-while-task-pending="update-email-images" — gate-blind (no task at capture). */
    readonly isUpdating = input(false);

    /** The gallery modal open flag (v1 `ft-targets-modal="modals.emailImages.{type}.preview"`). */
    readonly open = model(false);

    /** v1 `vm.onRefresh({image})` — the parent regenerates the image and finishes the task. */
    readonly refresh = output<any>();

    private readonly parse = inject(ParseService);
    private readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);
    private readonly ftTasks = inject(FtTasksService);
    private static readonly DEFAULT_LANGUAGE = 'en';

    /** v1 per-row `toolbar.isDesktop` (ng-init true). */
    private readonly desktopState = new Map<any, boolean>();

    /** v1 `vm.previewImage` ($watch images → defaultLanguageOrFirst → first processed). */
    protected readonly previewImage = computed<any>(() => {
        const images = this.images();
        if (!Array.isArray(images) || images.length === 0) {
            return null;
        }
        // v1 defaultLanguageOrFirst: find(code === default) || images[0].
        let preview = images.find((l) => l && l.code === FtEmailImagesPreviewComponent.DEFAULT_LANGUAGE) || images[0];
        // v1: if the default/first isn't processed, REPLACE with the first processed image (may be none).
        if (!preview || !this.parse.bool(preview.isImageProcessed)) {
            preview = images.find((img) => this.parse.bool(img.isImageProcessed));
        }
        return preview || null;
    });

    /** v1 `setupImagePrefix`: confirmation/poststay → 'large', prearrival → ''. */
    protected readonly cdnSuffixForDesktopImage = computed<string>(() =>
        this.emailImageType() === 'prearrival' ? '' : 'large'
    );

    /** v1 `setupImagePrefix`'s `headerTextKey` (the gallery modal header). */
    protected readonly headerTextKey = computed<string>(() => {
        switch (this.emailImageType()) {
            case 'confirmation':
                return 'flipto.account.ConfirmationBannerPreview';
            case 'poststay':
                return 'flipto.account.PoststayBannerPreview';
            default:
                return 'flipto.account.PrearrivalBannerPreview';
        }
    });

    protected isProcessed(img: any): boolean {
        return this.parse.bool(img && img.isImageProcessed);
    }
    protected hasMobile(img: any): boolean {
        return this.parse.bool(img && img.hasMobile);
    }

    /** v1 `image | ftAzureStoredPhoto:size` (size '' → no suffix). */
    protected azureUrl(photo: any, size: string): string | null {
        return buildAzureStoredPhotoUrl(photo, size ? size : null, this.environment.AzureCdnBaseUrl);
    }

    protected closeGallery(): void {
        this.open.set(false);
    }

    /** v1 per-row `toolbar.isDesktop` — defaults true unless explicitly toggled to mobile. */
    protected isDesktop(image: any): boolean {
        return this.desktopState.get(image) !== false;
    }

    protected setDesktop(image: any, value: boolean): void {
        this.desktopState.set(image, value);
    }

    /** v1 task key generate-email-image-{imageUUID}-{code} (the per-image refresh task). */
    protected generateKey(image: any): string {
        return 'generate-email-image-' + image.imageUUID + '-' + image.code;
    }

    /** v1 button ft-disable-while-task-pending + the loader ft-show-while-task-pending. */
    protected isImagePending(image: any): boolean {
        return this.ftTasks.isPending(this.generateKey(image));
    }

    /** v1 Refresh: ft-start-task (register) + ng-click vm.onRefresh (the parent finishes the key). */
    protected onGalleryRefresh(image: any): void {
        this.ftTasks.register(this.generateKey(image));
        this.refresh.emit(image);
    }
}
